import { cx } from 'cva';
import { type FunctionComponent, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Channel,
  MessageList,
  type ReactionOptions,
  Chat as StreamChat,
  MessageInput as StreamMessageInput,
  Thread,
  Window,
} from 'stream-chat-react';
import { errorToast, successToast } from '../../atoms/Toaster/Toaster.utils';
import { connectPrayerStreamUserSlice } from '../../core/async-action/connect-prayer-stream-user/connect-prayer-stream-user.slice';
import {
  UpdateUserGroupMembershipResponseStatus,
  updateUserGroupMembershipSlice,
} from '../../core/async-action/update-user-group-membership/update-user-group-membership.slice';
import { type RootState } from '../../core/root.state';
import { useContainer } from '../../hooks/useContainer';
import { useSelector } from '../../hooks/useSelector';
import { useTranslation } from '../../i18n/resources';
import { StreamService } from '../../services/stream';
import { ChannelHeader } from '../ChannelHeader/ChannelHeader';
import { EmptyChannel } from '../EmptyChannel/EmptyChannel';
import { Message } from '../Message/Message';
import { MessageInput } from '../MessageInput/MessageInput';
import { Sidebar } from '../Sidebar/Sidebar';
import { ThreadHeader } from '../ThreadHeader/ThreadHeader';
import styles from './Chat.module.css';

export type ChatProps = {
  className?: string;
};

export const Chat: FunctionComponent<ChatProps> = ({ className }) => {
  const dispatch = useDispatch();

  const { connected } = useSelector((state: RootState) => state.stream);
  const { client } = useContainer().get(StreamService);
  const { t } = useTranslation();
  const { status: updateUserGroupMembershipStatus } = useSelector(
    (state) => state.asyncAction.updateUserGroupMembershipState,
  );

  const defaultReactionOptions = useMemo<ReactionOptions>(() => {
    return [
      {
        Component: () => (
          <span className={styles.reaction}>
            {t('prayerStream', 'reaction_love_emoji')}
          </span>
        ),
        name: t('prayerStream', 'reaction_love_name'),
        type: 'love',
      },
      {
        Component: () => (
          <span className={styles.reaction}>
            {t('prayerStream', 'reaction_like_emoji')}
          </span>
        ),
        name: t('prayerStream', 'reaction_like_name'),
        type: 'like',
      },
      {
        Component: () => (
          <span className={styles.reaction}>
            {t('prayerStream', 'reaction_pray_emoji')}
          </span>
        ),
        name: t('prayerStream', 'reaction_pray_name'),
        type: 'praying',
      },
      {
        Component: () => (
          <span className={styles.reaction}>
            {t('prayerStream', 'reaction_sad_emoji')}
          </span>
        ),
        name: t('prayerStream', 'reaction_sad_name'),
        type: 'sad',
      },
      {
        Component: () => (
          <span className={styles.reaction}>
            {t('prayerStream', 'reaction_wow_emoji')}
          </span>
        ),
        name: t('prayerStream', 'reaction_wow_name'),
        type: 'wow',
      },
    ];
  }, [t]);

  useEffect(() => {
    dispatch(
      connectPrayerStreamUserSlice.actions.connectPrayerStreamUserRequested(),
    );
  }, [dispatch]);

  const { errorToastText, successToastText } = useMemo(() => {
    return {
      errorToastText: t('notifications', 'update_group_error'),
      successToastText: t('notifications', 'update_group_success'),
    };
  }, [t]);

  useEffect(() => {
    const handleClosePaymentMethodAddedAlert = (): void => {
      dispatch(
        updateUserGroupMembershipSlice.actions.updateUserGroupMembershipReset(),
      );
    };

    if (
      updateUserGroupMembershipStatus ===
      UpdateUserGroupMembershipResponseStatus.OTHER
    ) {
      errorToast({
        onAutoClose: handleClosePaymentMethodAddedAlert,
        onClose: handleClosePaymentMethodAddedAlert,
        title: errorToastText,
      });
    }
    if (
      updateUserGroupMembershipStatus ===
      UpdateUserGroupMembershipResponseStatus.SUCCESS
    ) {
      successToast({
        onAutoClose: handleClosePaymentMethodAddedAlert,
        onClose: handleClosePaymentMethodAddedAlert,
        title: successToastText,
      });
    }
  }, [
    updateUserGroupMembershipStatus,
    dispatch,
    errorToastText,
    successToastText,
  ]);

  if (!connected) {
    return null;
  }

  return (
    <div className={cx(className, styles.container)}>
      <StreamChat client={client}>
        <Sidebar />
        <Channel
          EmptyStateIndicator={EmptyChannel}
          Input={MessageInput}
          Message={Message}
          reactionOptions={defaultReactionOptions}
          ThreadHeader={ThreadHeader}
        >
          <div className={styles.window}>
            <Window>
              <ChannelHeader />
              <MessageList />
              <StreamMessageInput />
            </Window>
          </div>
          <Thread Input={MessageInput} Message={Message} />
        </Channel>
      </StreamChat>
    </div>
  );
};
